/**
 * Minimal SVG charts.
 *
 * Hand-rolled rather than pulling in a charting library: the Reports page needs
 * exactly two shapes, and a chart dependency would add more bundle than the
 * whole page. They inherit theme colours from CSS variables so they match the
 * rest of the console, and each carries a text summary for screen readers —
 * an SVG of bars is invisible to one otherwise.
 */

export type Point = { label: string; value: number };

function nice_max(values: number[]): number {
  const max = Math.max(0, ...values);
  if (max === 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  return Math.ceil(max / magnitude) * magnitude;
}

const inr = (n: number) =>
  n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : n >= 1000 ? `₹${(n / 1000).toFixed(1)}k` : `₹${n}`;

/**
 * Time series as an area + line. Values are plotted against a zero baseline so
 * the shape is honest — a truncated axis makes a flat week look like a cliff.
 */
export function TimeSeries({
  points,
  height = 160,
  format = (n: number) => String(n),
  label,
}: {
  points: Point[];
  height?: number;
  format?: (n: number) => string;
  label: string;
}) {
  if (points.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No data in this range.</p>;
  }
  const width = 720;
  const pad = { top: 8, right: 8, bottom: 22, left: 44 };
  const inner_w = width - pad.left - pad.right;
  const inner_h = height - pad.top - pad.bottom;
  const max = nice_max(points.map((p) => p.value));
  const step = points.length > 1 ? inner_w / (points.length - 1) : 0;

  const xy = points.map((p, i) => {
    const x = pad.left + (points.length === 1 ? inner_w / 2 : i * step);
    const y = pad.top + inner_h - (p.value / max) * inner_h;
    return [x, y] as const;
  });
  const line = xy.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${xy[xy.length - 1]![0].toFixed(1)},${pad.top + inner_h} L${xy[0]![0].toFixed(1)},${pad.top + inner_h} Z`;

  const total = points.reduce((s, p) => s + p.value, 0);
  const peak = points.reduce((a, b) => (b.value > a.value ? b : a));

  return (
    <figure className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[420px]" role="img" aria-label={`${label}. Total ${format(total)}. Highest ${peak.label} at ${format(peak.value)}.`}>
        {[0, 0.5, 1].map((f) => {
          const y = pad.top + inner_h - f * inner_h;
          return (
            <g key={f}>
              <line x1={pad.left} y1={y} x2={width - pad.right} y2={y} className="stroke-border" strokeWidth={1} />
              <text x={pad.left - 6} y={y + 4} textAnchor="end" className="fill-muted-foreground text-[10px]">
                {format(max * f)}
              </text>
            </g>
          );
        })}
        <path d={area} className="fill-primary/15" />
        <path d={line} className="stroke-primary" strokeWidth={2} fill="none" strokeLinejoin="round" />
        {/* Only label the ends — 30 dates would be unreadable at this width. */}
        <text x={pad.left} y={height - 6} className="fill-muted-foreground text-[10px]">{points[0]!.label}</text>
        <text x={width - pad.right} y={height - 6} textAnchor="end" className="fill-muted-foreground text-[10px]">
          {points[points.length - 1]!.label}
        </text>
      </svg>
    </figure>
  );
}

/** Horizontal bars for categories — readable however long the labels are. */
export function BarList({
  points,
  format = (n: number) => String(n),
  label,
}: {
  points: Point[];
  format?: (n: number) => string;
  label: string;
}) {
  if (points.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Nothing in this range.</p>;
  }
  const max = Math.max(...points.map((p) => p.value), 1);
  return (
    <ul className="flex flex-col gap-2" aria-label={label}>
      {points.map((p) => (
        <li key={p.label} className="grid grid-cols-[9rem_1fr_4rem] items-center gap-3 text-sm">
          <span className="truncate text-muted-foreground" title={p.label}>{p.label}</span>
          <span className="h-2.5 rounded-full bg-muted">
            <span
              className="block h-2.5 rounded-full bg-primary"
              style={{ width: `${Math.max(2, (p.value / max) * 100)}%` }}
            />
          </span>
          <span className="text-right tabular-nums font-medium">{format(p.value)}</span>
        </li>
      ))}
    </ul>
  );
}

export { inr };
